import { useMemo } from 'react';
import {
  Offer,
  OfferType,
  SessionFormat,
  ShortOfferDto,
  StudySubject,
  StudyType,
  useOffers,
} from '@app/api';
import { OfferItem } from './OfferItem';
import { applyDurationToPrice } from 'lib/priceComputation';
import { Emph } from './Emph';
import { Trans } from 'react-i18next';

interface Props {
  offer: Partial<Offer> & ShortOfferDto;
  credits?: number;
}

const getOfferRecruitmentCountForCredits = (offer: Offer, credits: number) => {
  const duration = offer.requirement.studyTypes.includes(StudyType.MODERATED)
    ? 60
    : 10;

  const price =
    offer.prices.perParticipant +
    applyDurationToPrice(offer.prices.perParticipantPerHour, duration) +
    offer.prices.eur;
  return Math.round(credits / price);
};

const components = { emph: <Emph /> };

export const OfferCreditItems = ({ offer, credits = 0 }: Props) => {
  const { data: offers } = useOffers();

  const equivalentValues = useMemo(() => {
    const b2cOffers = offers
      ?.filter(
        (offer) =>
          offer.type === OfferType.TARGETING &&
          !offer.requireCallToSales &&
          !offer.disabled &&
          offer.requirement.studyTypes.includes(StudyType.MODERATED) &&
          offer.requirement.studySubjects.includes(StudySubject.B2C) &&
          offer.requirement.formats?.includes(SessionFormat.VIDEO_CALL),
      )
      .sort((a, b) => {
        return a.prices.perParticipant - b.prices.perParticipant;
      });
    const unmoderatedOffer = offers?.find(
      (offer) =>
        offer.type === OfferType.TARGETING &&
        !offer.requireCallToSales &&
        !offer.disabled &&
        offer.requirement.studyTypes.includes(StudyType.UNMODERATED) &&
        offer.requirement.studySubjects.includes(StudySubject.B2C),
    );

    if (!b2cOffers?.length || !unmoderatedOffer) {
      return undefined;
    }
    return {
      minB2c: getOfferRecruitmentCountForCredits(
        b2cOffers[b2cOffers.length - 1],
        credits,
      ),
      maxB2c: getOfferRecruitmentCountForCredits(b2cOffers[0], credits),
      unmoderated: getOfferRecruitmentCountForCredits(
        unmoderatedOffer,
        credits,
      ),
    };
  }, [offer, credits]);

  return (
    <>
      <OfferItem
        text={
          <Trans
            i18nKey="billing.offers.block.items.creditsValidity"
            components={components}
          />
        }
      />
      {equivalentValues && credits > 0 && (
        <>
          {equivalentValues.maxB2c !== equivalentValues.minB2c ? (
            <OfferItem
              text={
                <Trans
                  i18nKey="billing.offers.block.items.consumerEquivalentMinMax"
                  components={components}
                  values={{
                    min: equivalentValues.minB2c,
                    max: equivalentValues.maxB2c,
                  }}
                />
              }
            />
          ) : (
            <OfferItem
              text={
                <Trans
                  i18nKey="billing.offers.block.items.consumerEquivalent"
                  components={components}
                  values={{
                    count: equivalentValues.minB2c,
                  }}
                />
              }
            />
          )}
          <OfferItem
            text={
              <Trans
                i18nKey="billing.offers.block.items.unmoderatedEquivalent"
                components={components}
                values={{ count: equivalentValues.unmoderated }}
              />
            }
          />
        </>
      )}
    </>
  );
};
